import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { usePricingFetch, fetchNextPage } from '../../hooks/usePricingFetch';
import { regions, formatPrice } from '../../config/pricingConfig';

const SQL_TIERS = [
  { value: 'General Purpose', label: 'General Purpose' },
  { value: 'Business Critical', label: 'Business Critical' },
  { value: 'Hyperscale', label: 'Hyperscale' },
];

const SQL_VCORES = [
  { value: '2', label: '2 vCores' },
  { value: '4', label: '4 vCores' },
  { value: '8', label: '8 vCores' },
  { value: '16', label: '16 vCores' },
  { value: '32', label: '32 vCores' },
];

const SQLPricing = () => {
  const [region, setRegion] = useState('');
  const [tier, setTier] = useState('');
  const [vcores, setVcores] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [extraItems, setExtraItems] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const filter = searchParams
    ? `armRegionName eq '${searchParams.region}' and serviceName eq 'SQL Database' and type eq 'Consumption' and contains(skuName, '${searchParams.tier}') and contains(skuName, '${searchParams.vcores} vCore')`
    : '';

  const { items, isLoading, error, nextPageLink } = usePricingFetch({
    queryKey: `sql-${filter}`,
    filter,
    enabled: !!searchParams,
  });

  const handleFetch = () => {
    setSearchParams({ region, tier, vcores });
    setExtraItems([]);
    setNextPageUrl(null);
  };

  const handleLoadMore = async () => {
    const url = nextPageUrl || nextPageLink;
    if (!url) return;
    setLoadingMore(true);
    try {
      const data = await fetchNextPage(url);
      setExtraItems((prev) => [...prev, ...(data.Items || [])]);
      setNextPageUrl(data.NextPageLink || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortIndicator = ({ col }) =>
    sortKey === col ? (sortAsc ? ' ↑' : ' ↓') : null;

  const allItems = [...items, ...extraItems];
  const sortedItems = sortKey
    ? [...allItems].sort((a, b) => {
        const va = a[sortKey]; const vb = b[sortKey];
        return sortAsc
          ? (typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb)))
          : (typeof vb === 'number' ? vb - va : String(vb).localeCompare(String(va)));
      })
    : allItems;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Azure SQL Database Pricing</h2>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Select Region" /></SelectTrigger>
          <SelectContent>{regions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Service Tier" /></SelectTrigger>
          <SelectContent>{SQL_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={vcores} onValueChange={setVcores}>
          <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="vCores" /></SelectTrigger>
          <SelectContent>{SQL_VCORES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={handleFetch} disabled={isLoading || !region || !tier || !vcores}>
          {isLoading ? 'Loading…' : 'Fetch Prices'}
        </Button>
      </div>
      {error && <div className="text-red-500 mb-4 text-sm">{error.message}</div>}
      {isLoading && <SkeletonTable rows={4} cols={4} />}
      {!isLoading && sortedItems.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-2">Showing {sortedItems.length} result(s)</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('skuName')}>SKU<SortIndicator col="skuName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('meterName')}>Meter<SortIndicator col="meterName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('productName')}>Product<SortIndicator col="productName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('unitPrice')}>Unit Price<SortIndicator col="unitPrice" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((price) => (
                <TableRow key={`${price.skuId}-${price.meterName}`}>
                  <TableCell>{price.skuName}</TableCell>
                  <TableCell>{price.meterName}</TableCell>
                  <TableCell>{price.productName}</TableCell>
                  <TableCell>{formatPrice(price.unitPrice, price.currencyCode, price.unitOfMeasure)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(nextPageUrl || nextPageLink) && (
            <div className="mt-4 text-center">
              <Button onClick={handleLoadMore} disabled={loadingMore} className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                {loadingMore ? 'Loading…' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SQLPricing;
